import { useState } from "react";
import { Loader2, Printer, X } from "lucide-react";

export const KIND_WORD = { shelf: "Shelf", scan: "Scan" };
const sizeWord = (s) => s.replace("x", "×");

// ONE ROW OF BIG TAPPABLE CHOICES — every step of the sheet looks the same
export function Choices({ id, title, options, value, onChange }) {
  return (
    <div className="space-y-1.5" data-testid={id}>
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{title}</div>
      <div className="flex flex-wrap gap-2">
        {options.map(([v, label, off]) => (
          <button key={String(v)} type="button" data-testid={`${id}-${v}`} disabled={!!off} title={off || undefined}
            onClick={() => onChange(v)}
            className={`rounded-xl border-2 px-3.5 py-2 text-sm text-left transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${value === v ? "border-[#E0781A] bg-orange-50 font-bold text-slate-900" : "border-slate-200 text-slate-700 hover:border-slate-300"}`}>
            {label}{off && <span className="block text-[11px] font-normal text-slate-500">{off}</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

function Pic({ item, onZoom, onPrintOne }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-1.5 hover:border-[#E0781A] transition-colors">
      <button type="button" data-testid={`print-labels-pic-${item.n}`} onClick={() => onZoom(item)} className="w-full text-left">
        <img src={item.png} alt={`${KIND_WORD[item.kind]} label ${item.code}`} className="w-full border border-slate-300 bg-white"
          style={{ aspectRatio: `${item.width} / ${item.height}`, imageRendering: "auto" }} />
        <div className="mt-1 text-[11px] text-slate-600 leading-tight">
          <b className="text-slate-900">{item.n}</b> · {KIND_WORD[item.kind]} · {sizeWord(item.size)}
          <div className="font-mono truncate">{item.code}</div>
        </div>
      </button>
      {onPrintOne && <PrintOneButton item={item} onPrintOne={onPrintOne} />}
    </div>
  );
}

// ONE TAP, ONE LABEL (his call, stability batch): print just this label — to hold it up, or scan it with the gun
function PrintOneButton({ item, onPrintOne, big }) {
  return (
    <button type="button" data-testid={`print-labels-one-${item.n}${big ? "-big" : ""}`} onClick={() => onPrintOne(item)}
      className={`mt-1 w-full inline-flex items-center justify-center gap-1 rounded-lg border border-slate-300 font-semibold text-slate-700 hover:border-[#E0781A] hover:text-[#C76714] transition-colors ${big ? "h-11 text-sm" : "h-9 text-[11px]"}`}>
      <Printer className={big ? "w-4 h-4" : "w-3.5 h-3.5"} />Print just this one
    </button>
  );
}

// STEP 3 — A PICTURE OF EVERY LABEL, drawn from the exact file the Zebra gets, in the order they come off the roll
export function LabelPics({ data, loading, onPrintOne }) {
  const [zoom, setZoom] = useState(null);
  if (loading && !data) return <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline" /> Drawing the labels…</div>;
  if (!data) return null;
  const close = data.items.some((i) => i.exact === false);
  return (
    <div className="space-y-2" data-testid="print-labels-preview">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
        3 · Preview — <span data-testid="print-labels-count">{data.count}</span> label{data.count === 1 ? "" : "s"}, in the order they print
        {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
      </div>
      <p className="text-[12px] text-slate-500">Drawn dot for dot from the file sent to the Zebra. Tap one to see it big.
        {close && " The small word strip on a holder's label uses the printer's own font, so it's drawn close, not exact."}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[46dvh] overflow-y-auto pr-1" data-testid="print-labels-grid">
        {data.items.map((i) => <Pic key={i.n} item={i} onZoom={setZoom} onPrintOne={onPrintOne} />)}
      </div>
      {zoom && (
        <div className="fixed inset-x-0 top-vv h-vv z-[60] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="print-labels-zoom" onClick={() => setZoom(null)}>
          <div className="bg-white rounded-xl p-3 max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2 text-sm">
              <span><b>{zoom.n}</b> · {KIND_WORD[zoom.kind]} label · {sizeWord(zoom.size)} · <span className="font-mono">{zoom.code}</span></span>
              <button type="button" data-testid="print-labels-zoom-close" onClick={() => setZoom(null)} className="p-1 text-slate-500"><X className="w-5 h-5" /></button>
            </div>
            <img src={zoom.png} alt={`${zoom.code} full size`} className="w-full border border-slate-300" />
            {onPrintOne && <PrintOneButton item={zoom} big onPrintOne={(it) => { setZoom(null); onPrintOne(it); }} />}
          </div>
        </div>
      )}
    </div>
  );
}
